// Client-side duration recompute — defense in depth.
//
// The backend orchestrator (plan_json_orchestrator.py:_recompute_durations)
// already recalculates duration_min from pace × km after the LLM responds,
// so plans generated from a7c39bf onwards arrive with correct times.
//
// This frontend layer exists to cover three cases where the backend value
// can't be trusted:
//   1. Plans persisted BEFORE the backend fix landed (DB still holds the
//      LLM's wrong number — e.g. 1:06 for an 18 km tempo at 5:00/km).
//   2. A future regression in the backend computation (paranoia).
//   3. Local mock data or imported plans that never went through the
//      backend at all.
//
// Algorithm matches the backend exactly:
//   day.duration_min = Σ segment.km × avg(segment.pace.min, segment.pace.max)
//   plan.estimated_duration_minutes = Σ day.duration_min
//
// We never mutate the input plan — return a shallow clone with patched
// numbers so React state updates work cleanly.

// "5:50" / "4:45" / "5:50-6:20" / "5:50–6:20" → seconds per km (averaged
// over all m:ss occurrences in the string). Returns null on bad input so
// the caller can fall back to whatever was already there.
function paceStringToSecPerKm(paceStr) {
  if (!paceStr || typeof paceStr !== 'string') return null;
  const matches = paceStr.match(/(\d+):(\d+)/g);
  if (!matches || matches.length === 0) return null;
  const seconds = matches.map((m) => {
    const [mm, ss] = m.split(':').map(Number);
    return mm * 60 + ss;
  });
  return seconds.reduce((a, b) => a + b, 0) / seconds.length;
}

// PaceRange object {min, max} → averaged seconds/km. Defensive on missing
// fields. Returns null if neither min nor max is parseable.
function paceRangeToSecPerKm(pace) {
  if (!pace) return null;
  // Already a string? handle "5:50–6:20" too.
  if (typeof pace === 'string') return paceStringToSecPerKm(pace);
  const lo = paceStringToSecPerKm(pace.min);
  const hi = paceStringToSecPerKm(pace.max);
  if (lo == null && hi == null) return null;
  if (lo == null) return hi;
  if (hi == null) return lo;
  return (lo + hi) / 2;
}

// Compute one day's duration in minutes. Prefers structure[] segments (more
// accurate when warmup/cooldown have their own pace) and falls back to
// day-level pace × day km when structure is missing.
function dayDurationMinutes(day) {
  if (day.is_rest) return 0;

  if (Array.isArray(day.structure) && day.structure.length > 0) {
    let totalSec = 0;
    let totalKm = 0;
    for (const seg of day.structure) {
      // An interval set with explicit steps (2026-10-19): each step at its own pace, so the
      // recoveries are timed at easy pace instead of the set's work pace.
      if (Array.isArray(seg.steps) && seg.steps.length > 0) {
        const parts = seg.steps.map(st => [paceRangeToSecPerKm(st.pace) || paceRangeToSecPerKm(seg.pace)
                                           || paceRangeToSecPerKm(day.pace), Number(st.km) || 0]);
        if (parts.every(([p]) => p != null)) {
          totalSec += parts.reduce((a, [p, k]) => a + p * k, 0);
          totalKm += Number(seg.km) || 0;
          continue;
        }
      }
      const segPace = paceRangeToSecPerKm(seg.pace) || paceRangeToSecPerKm(day.pace);
      const km = Number(seg.km) || 0;
      if (segPace == null || km <= 0) continue;
      totalSec += segPace * km;
      totalKm += km;
    }
    // Only trust the structure sum if it actually matches the day's km.
    if (totalSec > 0 && Math.abs(totalKm - (Number(day.km) || 0)) <= 1.0) {
      return Math.round(totalSec / 60);
    }
  }

  // Fallback: day-level pace × day km.
  const dayPace = paceRangeToSecPerKm(day.pace);
  const dayKm = Number(day.km) || 0;
  if (dayPace != null && dayKm > 0) {
    return Math.round((dayPace * dayKm) / 60);
  }

  // Last resort: keep whatever was there (could be a bad LLM value, but
  // better than zero on the UI).
  return Number(day.duration_min) || 0;
}

// Public entry — returns a NEW plan object with recomputed day durations
// and a recomputed estimated_duration_minutes on the top level. Safe to
// call with null / malformed plans (returns the input unchanged).
//
// Cycling pass-through: cycling days use '0:00' as a pace placeholder
// because the real intensity lives on pace.label ('FTP 88-94%'). Pace ×
// km would always come out as zero. The backend orchestrator sets
// duration_min directly from the LLM (validated as a reasonable
// integer) for cycling — we trust those values here.
export function recomputePlanDurations(plan) {
  if (!plan || !Array.isArray(plan.days)) return plan;
  if (plan.sport_type === 'cycling') return plan;

  let weekMin = 0;
  const days = plan.days.map((day) => {
    const min = dayDurationMinutes(day);
    weekMin += min;
    return { ...day, duration_min: min };
  });

  return {
    ...plan,
    days,
    // Keep the LLM's value when our recompute came up empty (e.g. all rest
    // days). Otherwise the WeekHeader 'זמן משוער' chip would show 0.
    estimated_duration_minutes:
      weekMin > 0 ? weekMin : plan.estimated_duration_minutes,
  };
}
